import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css'; // Import styles
import { LocateControl } from 'leaflet.locatecontrol'; // Import LocateControl
import L from 'leaflet';
import { useNavigate, useLocation } from 'react-router-dom';
// Import default marker icons
import markerIcon from '../assets/icons/RedMap.svg';
import markerIconRetina from '../assets/icons/RedMap.svg';
import HomeMap from '../assets/icons/HomeMap.svg';
import AddAddress from '../assets/icons/AddAddress.svg';
import OfficeIcon from '../assets/icons/OfficeList.svg';
import BussinessIcon from '../assets/icons/Bussiness.svg';
import OtherIcon from '../assets/icons/OtherList.svg';
import HomeIcon from '../assets/icons/HomeLIst.svg';
import ViewAddresses from '../assets/icons/ViewAddresses.svg'; // Add the icon for View Addresses
import ShareIcon from '../assets/icons/share.svg'; // Add the icon for Share
import CopyIcon from '../assets/icons/copy.svg'; // Add the icon for Copy
import CheckIcon from '../assets/icons/checked.svg'; // Add the icon for Check
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, TwitterShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import FavouriteTrue from '../assets/icons/favouritetrue.svg';
import FavouriteFalse from '../assets/icons/favouritefalse.svg';
import HomeAdd from '../assets/icons/HomeAdd.svg';
import OfficeAdd from '../assets/icons/OfficeAdd.svg';
import OtherAdd from '../assets/icons/OtherAdd.svg';
import { set } from 'react-hook-form';
// Configure Leaflet marker icons
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  iconSize: [25, 41], // Default size
  iconAnchor: [12, 41], // Anchor point for positioning
});

const { BaseLayer } = LayersControl;

L.Marker.prototype.options.icon = defaultIcon;

// Address and Props Types
interface Address {
  id: string;
  latitude: number;
  longitude: number;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressType: string;
  pinCode?: string;
  blockCode?: string;
  addressCode?: string;
  pamCode?: string;
  isFavourite?: boolean;
}


interface MapComponentProps {
  addresses?: Address[];
  boundaryData?: { latitude: number; longitude: number }[];
  showLocateControl?: boolean;
  onLocationChange?: (
    latitude: string,
    longitude: string,
    pinCode: string,
    street: string,
    city: string
  ) => void;
}

const LocateControlComponent = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const locateOptions = {
      position: 'topright' as L.ControlPosition,
      strings: {
        title: 'Show me where I am!'
      },
      locateOptions: {
        enableHighAccuracy: true,
        maxZoom: 16 // Set the maximum zoom level
      }
    };

    const locateControl = new LocateControl(locateOptions).addTo(map);

    locateControl.start();

    return () => {
      locateControl.stop();
      map.removeControl(locateControl);
    };
  }, [map]);

  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
  onLocationChange,
  addresses = [],
  boundaryData = [],
  showLocateControl = true,

}) => {
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([9.9921, 76.3019]);
  const [pincode, setPincode] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [addressResponse, setAddressResponse] = useState<Address[]>([]); // State to store API response
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null); // Track which pamCode has share options open
  const [copiedPamCode, setCopiedPamCode] = useState<string | null>(null); // State to track copied pamCode
  const isFavourite = location.pathname.includes('/favourites');
  const [addressResults, setAddressResults] = useState<Address[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const navigate = useNavigate();

  useEffect(() => {
    if (isFavourite) {
      setMarkerPosition([0, 0]); // Reset the marker position to remove it
    }
  }, [isFavourite]);

  const updateLocationDetails = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);

    // Fetch street, city, and pincode based on lat/lon using OpenStreetMap Nominatim API
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        const pinCode = data.address?.postcode || 'N/A';
        const street = data.address?.road || 'N/A';
        const city = data.address?.city || data.address?.town || data.address?.village || 'N/A';

        setPincode(pinCode);
        setStreet(street);
        setCity(city);

        if (onLocationChange) {
          onLocationChange(lat.toString(), lng.toString(), pinCode, street, city);
        }
      })
      .catch((error) => console.error('Error fetching location details:', error));
  };

  const defaultCenter: [number, number] =
    addresses.length > 0
      ? [addresses[0].latitude, addresses[0].longitude]
      : [9.9921, 76.3019]; // Default to a location if no addresses

  const goToMapAddAddress = (latitude: number, longitude: number) => {
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      addressLine1: street || '',
      addressLine2: city || '',
      addressLine3: `${street}, ${city}` || '',
      pinCode: pincode || ''
    });

    navigate(`/add-new-address?${params.toString()}`);
  };

  const fetchAddresses = async () => {
    setIsSubmitting(true);

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token not found in localStorage');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `https://pamsapi.vofoxsolutions.com/api/Address/GetPamCodeSuggessionAsync?latitude=${markerPosition[0]}&longitude=${markerPosition[1]}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        console.error('Error fetching data:', response.statusText);
        setIsSubmitting(false);
        return;
      }

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        if (data[0].addressLine1) {
          setAddressResponse(data);
        }
        else if (data[0].pinCode && data[0].blockCode) {
          setAddressResponse([
            {
              id: 'no-address',
              latitude: markerPosition[0],
              longitude: markerPosition[1],
              addressType: 'No Address Found',
              addressLine1: `Pincode: ${data[0].pinCode}`,
              addressLine2: `Block Code: ${data[0].blockCode}`,
              addressLine3: '',
              pinCode: data[0].pinCode,
              blockCode: data[0].blockCode,
              addressCode: '',
              isFavourite: false,
            },
          ]);
        } else {
          setAddressResponse([]);
        }
      } else {
        setAddressResponse([]);
      }

      setIsSubmitting(false);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddressResponse([]);
      setIsSubmitting(false);
    }
  };

  const toggleFavourite = (id?: string) => {
    if (!id) return;
    setAddressResponse((prev) =>
      prev.map((address) =>
        address.id === id ? { ...address, isFavourite: !address.isFavourite } : address
      )
    );
  };
  useEffect(() => {
    if (!isModalOpen) {
      setActiveSharePamCode(null);
    }
  }, [isModalOpen]);


  const HeartClick = async (addressId: string, isFavourite: boolean) => {
    const newFavourite = !isFavourite;

    setAddressResults((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: newFavourite } : item
      )
    );

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token not found. User might be logged out.');
      return;
    }

    try {
      if (newFavourite) {
        const apiUrl = 'https://pamsapi.vofoxsolutions.com/api/AddressFavourites/AddToFavouritesAsync';
        const payload = { addressId, isFavourite: true };

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          revertFavouriteState(addressId, isFavourite);
          return;
        }
      } else {
        const apiUrl = `https://pamsapi.vofoxsolutions.com/api/AddressFavourites/RemoveFromFavouritesAsync?addressId=${addressId}`;

        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to remove from favourites:', response.status, response.statusText);
          revertFavouriteState(addressId, isFavourite);
          return;
        }
      }
    } catch (error) {
      console.error('An error occurred while updating favourite status:', error);
      revertFavouriteState(addressId, isFavourite);
    }
  };

  const revertFavouriteState = (addressId: string, originalState: boolean) => {
    setAddressResults((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: originalState } : item
      )
    );
  };



  const openGoogleMaps = (latitude: number, longitude: number) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank");
  };
  const MapClickHandler = () => {
    const location = useLocation();
    const isFavourite = location.pathname.includes('/favourites');
    useMapEvents({
      click(e) {
        if (!isFavourite) {
          const { lat, lng } = e.latlng;
          updateLocationDetails(lat, lng);
        }
      },
    });
    return null;
  };

  const handleShareClick = (pamCode: string) => {
    if (activeSharePamCode === pamCode) {
      setActiveSharePamCode(null);
    } else {
      setActiveSharePamCode(pamCode);
    }
  };
  const shareMessage = (address: Address) => {
    return `Check out this address:\n${address?.addressLine1}, ${address?.addressLine2}, ${address?.addressLine3}\nPincode: ${address?.pinCode}\nPamcode: ${address?.pamCode}\nAddress Type: ${address?.addressType}\n\nMore details: https://getmethere.in`;
  };
  function shareBlockCodeMessage(pinCode: string, blockCode: string): string {
    return `${pinCode}-${blockCode}\n\nMore details: https://getmethere.in`;
  }

  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A"; // Handle null or undefined pamCode
    const pincode = pamCode.slice(0, 6); // First 6 characters as pincode
    const blockCode = pamCode.slice(6, 10); // Next 4 characters as block code
    const addressCode = pamCode.slice(10); // Remaining characters as address code

    return `${pincode}-${blockCode}-${addressCode}`; // Format as pincode-blockcode-addresscode
  };
  const handleCopy = async (pamCode: string) => {
    try {
      await navigator.clipboard.writeText(pamCode);
      setCopiedPamCode(pamCode);
      setTimeout(() => setCopiedPamCode(null), 2000);
      // toast.success('PAM code copied!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      // toast.error('Failed to copy!');
    }
  };

  const getAddressTypeIconHtml = (addressType: string) => {
    const type = addressType.toLowerCase();
    if (type === 'home') {
      return `<img src="${HomeAdd}" alt="Home" style="width:60px; height:60px;" />`;
    } else if (type === 'office') {
      return `<img src="${OfficeAdd}" alt="Office" style="width:60px; height:60px;" />`;
    } else {
      // For any other type (including 'other')
      return `<img src="${OtherAdd}" alt="Other" style="width:60px; height:60px;" />`;
    }
  };
  // Function to determine if the input corresponds to 6-digit or 10-digit code
  const getMapComponent = () => {
    if (addresses.length > 0) {
      const firstAddress = addresses[0];

      if (firstAddress.pinCode && firstAddress.pinCode.length === 6) {
        // Render PolygonMapComponent when pinCode is 6 digits (e.g., addresses)
        return (
          <MapContainer center={defaultCenter} zoom={13} className="h-full w-full" style={{ zIndex: 10 }}>
            <LayersControl position="topright">
              <LayersControl.BaseLayer name="Default Map" checked>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            {addresses.map((address) => {
              const customIcon = L.divIcon({
                html: getAddressTypeIconHtml(address.addressType || 'other'),
                className: '', // Remove any default styles
                iconSize: [30, 30],
              });
              return (
                <Marker
                  key={address.id || `${address.latitude}-${address.longitude}`}
                  position={[address.latitude, address.longitude]}
                  icon={customIcon}
                  eventHandlers={{
                    click: () => openGoogleMaps(address.latitude, address.longitude),
                  }}
                />
              );
            })}

            {showLocateControl && <LocateControlComponent />}
          </MapContainer>
        );
      } else if (firstAddress.pinCode && firstAddress.pinCode.length === 10) {
        // Render PolygonLockMapComponent for 10-digit pinCode (e.g., boundary data)
        return (
          <MapContainer center={defaultCenter} zoom={13} className="h-full w-full">
            <LayersControl position="topright">
              <LayersControl.BaseLayer name="Default Map" checked>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            {boundaryData.map((boundary, index) => (
              <Marker
                key={index}
                position={[boundary.latitude, boundary.longitude]}
                eventHandlers={{
                  click: () => goToMapAddAddress(boundary.latitude, boundary.longitude),
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
                  <span>Boundary</span>
                </Tooltip>

                <Popup>
                  <div>
                    <strong>Boundary Latitude:</strong> {boundary.latitude}<br />
                    <strong>Boundary Longitude:</strong> {boundary.longitude}<br />
                  </div>
                </Popup>
              </Marker>
            ))}
            {showLocateControl && <LocateControlComponent />}
          </MapContainer>
        );
      }
    }

    // Default case: If pinCode is not 6 or 10 digits, show a general MapComponent
    return (
      <MapContainer center={defaultCenter} zoom={13} className="h-full w-full">
         <LayersControl position="topright">
         <LayersControl.BaseLayer name="Default Map" checked>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
         </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        {addresses.map((address) => (
          <Marker
            key={address.id || `${address.latitude}-${address.longitude}`}
            position={[address.latitude, address.longitude]}
            eventHandlers={{
              click: () => goToMapAddAddress(address.latitude, address.longitude),
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
              <span>{address.addressLine3}</span>
            </Tooltip>

            <Popup>
              <div>
                <strong>Address:</strong> {address.addressLine1}, {address.addressLine2}<br />
                <strong>{address.addressLine3}</strong><br />
                <strong>Pincode:</strong> {address.pinCode}<br />
              </div>
            </Popup>
          </Marker>
        ))}
        {showLocateControl && <LocateControlComponent />}
        <MapClickHandler />

        <Marker
          position={markerPosition}
          eventHandlers={{
            click: () => {
              const [latitude, longitude] = markerPosition;
              // goToMapAddAddress(latitude, longitude);
            },
          }}
        >
          {/* <Popup>
            <span>Click to add address</span>
          </Popup> */}

          {((street || city || pincode) && !isFavourite) && (
            <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent interactive>
              <div className="flex flex-col gap-4 min-w-[250px] max-w-[350px] bg-gradient-to-r from-white to-blue-50 rounded-xl p-4 shadow-2xl">
                {/* Address Display */}
                <div className="max-h-[120px] overflow-y-auto">
                  <p className="text-md font-semibold text-gray-800 break-words whitespace-pre-wrap">
                    {street}, {city}, {pincode}
                  </p>
                </div>
                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-3 border-t border-gray-200 pt-3">
                  {/* Navigate Button */}
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 rounded-lg bg-white hover:bg-blue-100 border border-transparent hover:border-blue-300 transition-all duration-300 group shadow-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const [lat, lng] = markerPosition;
                      window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
                    }}
                  >
                    <span className="text-base font-medium text-blue-600 truncate mr-3 group-hover:text-blue-700">
                      Navigate
                    </span>
                    <img
                      src={HomeMap}
                      alt="Map"
                      className="w-6 h-6 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300"
                    />
                  </button>
                  {/* Add Address Button */}
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 rounded-lg bg-white hover:bg-blue-100 border border-transparent hover:border-blue-300 transition-all duration-300 group shadow-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const [lat, lng] = markerPosition;
                      goToMapAddAddress(lat, lng);
                    }}
                  >
                    <span className="text-base font-medium text-blue-600 truncate mr-3 group-hover:text-blue-700">
                      Add Address
                    </span>
                    <img
                      src={AddAddress}
                      alt="Add"
                      className="w-6 h-6 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300"
                    />
                  </button>
                  {/* View Addresses Button */}
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 rounded-lg bg-white hover:bg-blue-100 border border-transparent hover:border-blue-300 transition-all duration-300 group shadow-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchAddresses(); // Trigger the API call to fetch addresses
                    }}
                  >
                    <span className="text-base font-medium text-blue-600 truncate mr-3 group-hover:text-blue-700">
                      {isSubmitting ? 'View...' : 'View'}
                    </span>
                    <img
                      src={ViewAddresses}
                      alt="View"
                      className="w-6 h-6 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300"
                    />
                  </button>

                </div>
              </div>

            </Tooltip>
          )}
        </Marker>
      </MapContainer>
    );
  };

  return (
    <div className="h-full relative flex">
      <div className="flex-1 relative z-0">
        {getMapComponent()}
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1000] transition-opacity animate-fade-in">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl p-8 relative transform scale-100 transition-transform animate-scale-in">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-4 mb-6">
              <h2 className="text-3xl font-bold text-blue-600 tracking-wide">📍 Address Details</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-blue-500 hover:text-blue-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-96 overflow-y-auto space-y-6">
              {addressResponse.length === 0 ? (
                <div className="text-center text-xl text-gray-500 p-6">
                  <p>No address available</p>
                </div>
              ) : addressResponse[0]?.id === 'no-address' ? (
                // Container for the block code with a share icon
                <div className="relative text-center text-xl text-gray-700 p-6">
                  {/* Displayed block code */}
                  <p>
                    🏢 <strong>Block Code:</strong>{" "}

                    {addressResponse[0]?.pinCode}-{addressResponse[0]?.blockCode}
                  </p>
                  {/* Share icon positioned at the top right corner */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Build the block code string
                      const blockCodeStr = `${addressResponse[0]?.pinCode}-${addressResponse[0]?.blockCode}`;
                      // Toggle the share panel: if already open, close it; otherwise, open it.
                      setActiveSharePamCode((prev) =>
                        prev === blockCodeStr ? null : blockCodeStr
                      );
                    }}
                    title="Share"
                    className="absolute top-2 right-2 p-1 border border-[#D6DFFE] bg-blue-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none"
                  >
                    <img src={ShareIcon} alt="Share" className="w-4 h-4" />
                  </button>
                  {/* Share panel positioned below the share icon */}
                  {activeSharePamCode === `${addressResponse[0]?.pinCode}-${addressResponse[0]?.blockCode}` && (
                    <div className="absolute rounded-lg  flex gap-4 transform  right-2 max-h-40">
                      <EmailShareButton
                        url={shareBlockCodeMessage(
                          addressResponse[0]?.pinCode ?? '',
                          addressResponse[0]?.blockCode ?? ''
                        )}
                        className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                      >
                        <EmailIcon size={32} round />
                      </EmailShareButton>

                      <WhatsappShareButton
                        url={shareBlockCodeMessage(
                          addressResponse[0]?.pinCode ?? '',
                          addressResponse[0]?.blockCode ?? ''
                        )} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                      >
                        <WhatsappIcon size={32} round />
                      </WhatsappShareButton>
                      <FacebookShareButton
                        url={shareBlockCodeMessage(
                          addressResponse[0]?.pinCode ?? '',
                          addressResponse[0]?.blockCode ?? ''
                        )} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                      >
                        <FacebookIcon size={32} round />
                      </FacebookShareButton>
                      <TwitterShareButton
                        url={shareBlockCodeMessage(
                          addressResponse[0]?.pinCode ?? '',
                          addressResponse[0]?.blockCode ?? ''
                        )} className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                      >
                        <TwitterIcon size={32} round />
                      </TwitterShareButton>
                    </div>

                  )}
                  <div className='mt-10'>
                    <p className='text-sm'>Address are not Added in this block </p>
                  </div>
                </div>

              ) : (
                // ... your mapping branch for addresses remains unchanged.
                addressResponse.map((address) => {
                  const pamCode = `${address.pinCode}${address.blockCode}${address.addressCode}`;
                  return (
                    <div
                      key={address.id}
                      className="p-6 bg-gradient-to-r from-blue-50 to-white rounded-2xl shadow-md hover:shadow-xl transition-all"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent click propagation
                        setActiveSharePamCode(null); // Hide the share panel when clicking outside
                      }}
                    >
                      <div className="grid grid-cols-10 mb-4 gap-4 place-content-between">
                        {/* Address Type Field */}
                        <div className="text-sm col-span-9 gap-2 md:text-base font-bold text-gray-700 flex items-center">
                          {address.addressType.toLowerCase() === 'office' && (
                            <>
                              <img
                                src={OfficeIcon}
                                alt="Office Icon"
                                className="w-6 h-6 mr-2 "
                              />
                              Office
                            </>
                          )}
                          {/* {address.addressType.toLowerCase() === 'business' && (
                            <>
                              <img
                                src={BussinessIcon}
                                alt="Business Icon"
                                className="w-6 h-6 mr-2 rounded-full ring-1 ring-offset-4 ring-green-500"
                              />
                              Business
                            </>
                          )} */}
                          {address.addressType.toLowerCase() === 'home' && (
                            <>
                              <img
                                src={HomeIcon}
                                alt="Home Icon"
                                className="w-8 h-8 mr-2 "
                              />
                              Home
                            </>
                          )}
                          {address.addressType.toLowerCase() === 'other' && (
                            <>
                              <img
                                src={OtherIcon}
                                alt="Other Icon"
                                className="w-8 h-8 mr-2 "
                              />
                              Other
                            </>
                          )}
                          {!['office', 'home', 'other'].includes(address.addressType.toLowerCase()) && (
                            <>
                              <img
                                src={OtherIcon}
                                alt="Custom Type Icon"
                                className="w-8 h-8 mr-2 "
                              />
                              {address.addressType}
                            </>
                          )}
                        </div>
                        <div className="flex items-start justify-end col-span-1 relative">
                          {/* Share Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Toggle share panel based on the pamCode
                              setActiveSharePamCode((prevPamCode) =>
                                prevPamCode === pamCode ? null : pamCode
                              );
                            }}
                            title="Share"
                            className="p-1 border border-[#D6DFFE] bg-blue-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0"
                          >
                            <img src={ShareIcon} alt="Share" className="w-4 h-4" />
                          </button>
                          {/* Favourite Button */}
                          {address && address.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavourite(address.id);
                                HeartClick(address.id, address.isFavourite ?? false);
                              }}
                              title="Favourite"
                              className="p-1 ml-2 border border-[#D6DFFE] bg-blue-50 rounded-md hover:bg-[#c4d4fc] focus:outline-none flex-shrink-0 z-30"
                            >
                              <img
                                src={address.isFavourite ? FavouriteTrue : FavouriteFalse}
                                alt="Favourite"
                                className="w-4 h-4"
                              />
                            </button>
                          )}
                          {/* Share Panel for mapped addresses */}
                          {activeSharePamCode === pamCode && address && (
                            <div className="absolute z-20 p-4 mt-8 bg-white shadow-lg rounded-lg border border-gray-200 flex gap-4 transform scale-105 right-0">
                              <EmailShareButton
                                url={shareMessage(address)}
                                className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                              >
                                <EmailIcon size={32} round />
                              </EmailShareButton>
                              <WhatsappShareButton
                                url={shareMessage(address)}
                                className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                              >
                                <WhatsappIcon size={32} round />
                              </WhatsappShareButton>
                              <FacebookShareButton
                                url={shareMessage(address)}
                                className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                              >
                                <FacebookIcon size={32} round />
                              </FacebookShareButton>
                              <TwitterShareButton
                                url={shareMessage(address)}
                                className="transform transition-transform duration-300 hover:scale-125 hover:shadow-xl"
                              >
                                <TwitterIcon size={32} round />
                              </TwitterShareButton>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-semibold text-blue-500">🏠 Building/House Number:</span>
                        <span className="ml-3 text-blue-700">{address.addressLine1}</span>
                      </div>
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-semibold text-blue-500">🏡 Street:</span>
                        <span className="ml-3 text-blue-700">{address.addressLine2}</span>
                      </div>
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-semibold text-blue-500">🏢 City:</span>
                        <span className="ml-3 text-blue-700">{address.addressLine3}</span>
                      </div>
                      <div className="flex items-center relative">
                        <input
                          type="text"
                          className="w-full p-2 border rounded-md pr-10"
                          value={formatPamCode(pamCode)}
                          readOnly
                        />
                        <button
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                          onClick={() => handleCopy(pamCode)}
                        >
                          {copiedPamCode === address.pamCode ? (
                            <img src={CheckIcon} alt="Copied" className="w-6 h-6" />
                          ) : (
                            <img src={CopyIcon} alt="Copy" className="w-6 h-6" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>




            {/* Footer with Close Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>



      )}
    </div>
  );
};

export default MapComponent;




